import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAccessToken } from './utils/authToken';
import { readCachedAccessStatus } from './utils/accessStatusCache';
import { readCachedOnboardingCompletion, routeToNextStep } from './utils/flowGuards';
import { signedOutRedirectFor } from './utils/postAuthPath';
import { withIncomingSearch } from './utils/landingQuery';
import { isPublicAppPath } from './utils/publicPaths';
import './flowGuard.css';

const DASHBOARD_CHAT_HANDOFF_STORAGE_KEY = 'dople_dashboard_chat_handoff_v1';

const INTEGRATION_CALLBACK_PATHS = new Set([
  '/plaid/callback',
  '/strava/callback',
  '/oura/callback',
  '/spotify/callback',
  '/google-calendar/callback',
]);

const verifiedCompletedSession = () => {
  if (readCachedOnboardingCompletion() !== true) return false;
  const cached = readCachedAccessStatus({ token: getAccessToken() || '' });
  return Boolean(cached?.surveyCompleted);
};

const hasDashboardChatHandoff = (location) => {
  if (location?.state?.dashboardHandoff || location?.state?.seedPrompt || location?.state?.dashboardSource) {
    return true;
  }
  try {
    const raw = window.sessionStorage.getItem(DASHBOARD_CHAT_HANDOFF_STORAGE_KEY);
    return Boolean(raw);
  } catch {
    return false;
  }
};

const loadingTextForPath = (location) => {
  if (location?.pathname === '/chat' && hasDashboardChatHandoff(location)) {
    return 'Opening this pick...';
  }
  if (location?.pathname === '/chat') {
    return 'Opening chat...';
  }
  return 'Opening...';
};

export default function FlowGuard({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isIntegrationCallback = INTEGRATION_CALLBACK_PATHS.has(location.pathname);
  const hasCachedCompletedOnboarding = verifiedCompletedSession();
  const loadingText = loadingTextForPath(location);
  const [allowRender, setAllowRender] = useState(isIntegrationCallback || hasCachedCompletedOnboarding);
  const [checking, setChecking] = useState(!isIntegrationCallback && !hasCachedCompletedOnboarding);
  const [routeError, setRouteError] = useState('');

  const runGuard = useCallback(async () => {
    if (INTEGRATION_CALLBACK_PATHS.has(location.pathname)) {
      setRouteError('');
      setAllowRender(true);
      setChecking(false);
      return;
    }

    const canRenderWhileChecking = verifiedCompletedSession();
    setChecking(!canRenderWhileChecking);
    setRouteError('');
    setAllowRender(canRenderWhileChecking);

    try {
      const result = await routeToNextStep(navigate, {
        replace: true,
        currentPath: location.pathname,
        search: location.search,
        hash: location.hash,
      });

      const target = result?.next || location.pathname;
      if (target !== location.pathname) {
        navigate(withIncomingSearch(target, location.search, location.hash), { replace: true });
        return;
      }

      setAllowRender(true);
    } catch (error) {
      console.error('[FlowGuard] access check failed:', error);
      if (!getAccessToken()) {
        if (isPublicAppPath(location.pathname)) {
          setAllowRender(true);
          return;
        }
        navigate(withIncomingSearch(
          signedOutRedirectFor(location.pathname, location.search, location.hash),
          location.search,
          location.hash,
        ), { replace: true });
        return;
      }
      setRouteError(
        'We could not verify your account access. Check your connection, then retry.'
      );
      setAllowRender(false);
    } finally {
      setChecking(false);
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    void runGuard();
  }, [runGuard]);

  if (routeError && !checking) {
    return (
      <div className="flow-guard-loading" role="alert">
        <p className="flow-guard-loading__text">{routeError}</p>
        <button type="button" className="flow-guard-loading__retry" onClick={() => void runGuard()}>
          Retry
        </button>
      </div>
    );
  }

  if (checking || !allowRender) {
    return (
      <div className="flow-guard-loading" role="status" aria-live="polite">
        <div className="flow-guard-loading__spinner" aria-hidden="true" />
        <p className="flow-guard-loading__text">{loadingText}</p>
      </div>
    );
  }

  return children;
}
